import { useQuery, type UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { vestigasApi } from '~/services/kyClient';

import {
  type DataSubscription,
  type DataSubscriptionCreatedValues,
  DataSubscriptionObject,
} from '~/models/dataSubscriptions';

import { queryKeysDataSubscription } from './queryKeys';

type DataSubscriptionResponse = {
  items: DataSubscription[];
};

/**
 * Fetches a list of data subscriptions from the API.
 * This function retrieves data subscriptions and optionally maps them into instances of 'DataSubscription'
 * if 'initDataSubscriptions' is true.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/Data-Subscription/operation/get_all_data_subscriptions_data_subscription_get
 * @returns {Promise<DataSubscriptionResponse>} Response from the server.
 * @throws {Error} If the API request fails.
 */
export const fetchDataSubscriptions = async (initDataSubscriptions = true) => {
  const response = await vestigasApi
    .get(ENDPOINT.DATA_SUBSCRIPTION.GET())
    .json<DataSubscriptionResponse>();

  if (initDataSubscriptions) {
    return (
      response.items?.map((item) => DataSubscriptionObject.create(item)) ?? []
    );
  }

  return response.items ?? [];
};

/**
 * Custom React Query hook to fetch data subscriptions.
 */
export const useQueryDataSubscriptions = (
  initDataSubscriptions = true,
  options?: Omit<
    UseQueryOptions<DataSubscription[] | DataSubscriptionCreatedValues[]>,
    'queryKey' | 'queryFn'
  >,
) =>
  useQuery({
    queryFn: async () => fetchDataSubscriptions(initDataSubscriptions),
    queryKey: queryKeysDataSubscription.getAllDataSubscription(
      initDataSubscriptions,
    ),
    ...options,
  });
